/* === iFungi: keyboard support for the LF7 / PS7 custom purchase dropdowns ===
   2026-08-15.

   PROBLEM (see ../buybox-keyboard-accessibility.md), verified against the real
   markup in snippets/lf7-buybox-right-col.liquid and sections/ps7-buybox.liquid:

   The ARIA scaffolding is already correct in both buy boxes. The wrapper carries
   data-cdd-target and aria-expanded, the trigger is a real <button> with
   aria-haspopup="listbox" and a localized aria-label, the panel is
   <ul role="listbox" hidden>, and the native select already has BOTH
   tabindex="-1" and aria-hidden="true" — so it is correctly out of the tab order
   and out of the accessibility tree.

   What is missing is the keyboard behaviour itself. The flyout builds options as
   <li role="option"> with no tabindex and only a click handler, and there is no
   keydown handling anywhere. A keyboard user can focus the trigger and open the
   panel (Enter/Space activate the button), but cannot move to or choose any
   option — and because the native select is properly removed from the tab order,
   there is no fallback path. The control is keyboard-inoperable (WCAG 2.1.1).

   APPROACH: additive. This file does not modify the buy box scripts; it layers
   the listbox keyboard contract on top of the markup they already produce, using
   the aria-activedescendant pattern (focus stays on the trigger button, so there
   is no focus-restoration problem when the panel is rebuilt).

   The buy box scripts rebuild the panel's innerHTML on almost every state change
   (rebuildPanel), which discards element ids. Ids are therefore assigned lazily
   at interaction time rather than once at init.

   Active-option highlighting is applied as an inline style because no CSS class
   exists for it, and shipping this as a self-contained asset avoids touching the
   43 KB / 34 KB buy box stylesheets.

   2026-10-04: the wrapper's open/closed state now lives in data-expanded (aria-expanded is
   not a permitted attribute on a plain <div>; the trigger <button> keeps aria-expanded).

   2026-10-13 (audit): screen-reader fixes. aria-activedescendant is only honoured on a
   combobox, not a plain button, so options were never announced; the trigger is now a
   select-only combobox (role="combobox", ARIA 1.2). Its name came from a fixed aria-label
   ("Select quantity") that hid the current choice; it is now labelled by the visible
   label plus the selected value. Clicking the visible label focuses the trigger instead of
   the hidden native select. Sold-out options (aria-disabled) can be reached but not chosen.

   STATUS: UNTESTED in a browser. Verify against a preview before publishing. */
(function () {
  if (window._ifungiBuyboxA11yBound) return;
  window._ifungiBuyboxA11yBound = true;

  var CDD = '.lf7-cdd, .ps7-cdd';
  var ACTIVE_STYLE = { outline: '2px solid #6B4226', outlineOffset: '-2px', background: '#f2ede6' };
  var uid = 0;

  function partsOf(cdd) {
    var p = cdd.classList.contains('lf7-cdd') ? 'lf7' : 'ps7';
    return {
      prefix: p,
      sel: cdd.querySelector('select.' + p + '-cdd-native'),
      btn: cdd.querySelector('.' + p + '-cdd-trigger'),
      panel: cdd.querySelector('.' + p + '-cdd-panel'),
      optSel: '.' + p + '-cdd-option'
    };
  }

  function isOpen(cdd) {
    return cdd.getAttribute('data-expanded') === 'true';
  }

  function options(parts) {
    return parts.panel ? Array.prototype.slice.call(parts.panel.querySelectorAll(parts.optSel)) : [];
  }

  function paint(opts, activeIdx) {
    for (var i = 0; i < opts.length; i++) {
      var on = i === activeIdx;
      for (var k in ACTIVE_STYLE) {
        if (Object.prototype.hasOwnProperty.call(ACTIVE_STYLE, k)) {
          opts[i].style[k] = on ? ACTIVE_STYLE[k] : '';
        }
      }
    }
  }

  /* Assign ids lazily — rebuildPanel() wipes innerHTML and takes ids with it. */
  function ensureIds(cdd, opts) {
    if (!cdd.id) cdd.id = 'ifungi-cdd-' + ++uid;
    for (var i = 0; i < opts.length; i++) {
      if (!opts[i].id) opts[i].id = cdd.id + '-opt-' + i;
    }
  }

  function selectedIndex(parts, opts) {
    var v = parts.sel ? parts.sel.value : null;
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].getAttribute('data-value') === v) return i;
    }
    return 0;
  }

  function setActive(cdd, parts, opts, idx) {
    if (!opts.length) return -1;
    if (idx < 0) idx = 0;
    if (idx > opts.length - 1) idx = opts.length - 1;
    ensureIds(cdd, opts);
    paint(opts, idx);
    if (parts.btn) parts.btn.setAttribute('aria-activedescendant', opts[idx].id);
    if (opts[idx].scrollIntoView) {
      try { opts[idx].scrollIntoView({ block: 'nearest' }); } catch (e) {}
    }
    cdd._ifungiActive = idx;
    return idx;
  }

  function activeIndex(cdd, parts, opts) {
    var i = cdd._ifungiActive;
    if (typeof i !== 'number' || i < 0 || i > opts.length - 1) return selectedIndex(parts, opts);
    return i;
  }

  function closePanel(cdd, parts) {
    cdd.setAttribute('data-expanded', 'false');
    if (parts.btn) {
      parts.btn.setAttribute('aria-expanded', 'false');
      parts.btn.removeAttribute('aria-activedescendant');
    }
    if (parts.panel) parts.panel.hidden = true;
    paint(options(parts), -1);
    cdd._ifungiActive = -1;
  }

  /* 2026-10-15 (PS7 audit): the one-time pack list can be held open by the buy box
     (<prefix>-qty-open). Escape must close it on screen too, otherwise the list stays
     visible while it tells screen readers it is closed. Tab leaves a held-open list
     open (and announced as open) and only clears the keyboard highlight. */
  function heldOpenWrap(cdd, parts) {
    var qtyWrap = document.getElementById(parts.prefix + '-qty-onetime-wrap');
    if (!qtyWrap || !qtyWrap.contains(cdd)) return null;
    return qtyWrap.classList.contains(parts.prefix + '-qty-open') ? qtyWrap : null;
  }

  function clearActive(cdd, parts) {
    if (parts.btn) parts.btn.removeAttribute('aria-activedescendant');
    paint(options(parts), -1);
    cdd._ifungiActive = -1;
  }

  function openPanel(cdd, parts) {
    cdd.setAttribute('data-expanded', 'true');
    if (parts.btn) parts.btn.setAttribute('aria-expanded', 'true');
    if (parts.panel) parts.panel.hidden = false;
    var opts = options(parts);
    setActive(cdd, parts, opts, selectedIndex(parts, opts));
  }

  /* Mirror what the buy box's own panel click handler does, so keyboard and
     pointer selection go through exactly the same downstream path: set the
     native select, fire change (the buy box listens and rebuilds), close. */
  function commit(cdd, parts, opts, idx) {
    var li = opts[idx];
    if (!li || !parts.sel) return;
    if (li.getAttribute('aria-disabled') === 'true') return; /* sold out: stay open on it */
    var val = li.getAttribute('data-value');
    if (parts.sel.value !== val) {
      parts.sel.value = val;
      parts.sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    closePanel(cdd, parts);
    var qtyWrap = document.getElementById(parts.prefix + '-qty-onetime-wrap');
    if (qtyWrap) qtyWrap.classList.remove(parts.prefix + '-qty-open');
    if (parts.btn) parts.btn.focus();
  }

  function onKeydown(e) {
    var cdd = e.target && e.target.closest ? e.target.closest(CDD) : null;
    if (!cdd) return;
    var parts = partsOf(cdd);
    if (!parts.btn || !parts.panel || !parts.sel) return;

    var key = e.key;
    var open = isOpen(cdd);
    var opts = options(parts);

    if (key === 'Escape') {
      if (!open) return;
      e.preventDefault();
      e.stopPropagation();
      var heldWrap = heldOpenWrap(cdd, parts);
      if (heldWrap) heldWrap.classList.remove(parts.prefix + '-qty-open');
      closePanel(cdd, parts);
      parts.btn.focus();
      return;
    }

    if (!open) {
      /* Enter and Space are deliberately not handled here: the trigger is a
         <button>, so the browser turns them into a click and the buy box's own
         handler opens the panel. Intercepting would double-toggle it. */
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End') {
        e.preventDefault();
        openPanel(cdd, parts);
      }
      return;
    }

    var idx = activeIndex(cdd, parts, opts);

    if (key === 'ArrowDown') { e.preventDefault(); setActive(cdd, parts, opts, idx + 1); return; }
    if (key === 'ArrowUp')   { e.preventDefault(); setActive(cdd, parts, opts, idx - 1); return; }
    if (key === 'Home')      { e.preventDefault(); setActive(cdd, parts, opts, 0); return; }
    if (key === 'End')       { e.preventDefault(); setActive(cdd, parts, opts, opts.length - 1); return; }

    if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
      /* preventDefault stops the button's synthesised click, which would
         otherwise toggle the panel shut instead of selecting. */
      e.preventDefault();
      e.stopPropagation();
      commit(cdd, parts, opts, idx);
      return;
    }

    if (key === 'Tab') {
      if (heldOpenWrap(cdd, parts)) { clearActive(cdd, parts); return; }
      closePanel(cdd, parts);
    }
  }

  function wire(cdd) {
    var parts = partsOf(cdd);
    if (!parts.sel || !parts.btn || !parts.panel) return false;

    parts.panel.setAttribute('role', 'listbox');
    if (!parts.panel.id) {
      if (!cdd.id) cdd.id = 'ifungi-cdd-' + ++uid;
      parts.panel.id = cdd.id + '-panel';
    }
    parts.btn.setAttribute('role', 'combobox');
    parts.btn.setAttribute('aria-haspopup', 'listbox');
    parts.btn.setAttribute('aria-controls', parts.panel.id);
    if (!parts.btn.hasAttribute('aria-expanded')) {
      parts.btn.setAttribute('aria-expanded', String(isOpen(cdd)));
    }

    /* Name the listbox from the trigger, which already carries a localized
       aria-label from Liquid. An aria-label hardcoded here would be English on
       the French storefront. */
    if (!parts.btn.id) {
      if (!cdd.id) cdd.id = 'ifungi-cdd-' + ++uid;
      parts.btn.id = cdd.id + '-trigger';
    }
    if (!parts.panel.hasAttribute('aria-label') && !parts.panel.hasAttribute('aria-labelledby')) {
      parts.panel.setAttribute('aria-labelledby', parts.btn.id);
    }

    /* Name = visible label + current selection, e.g. "QUANTITY 2 Bags $189.95 …".
       Keep the Liquid aria-label only when there is no visible label to point at. */
    var wrap = cdd.parentElement;
    var label = null;
    if (wrap) {
      for (var c = 0; c < wrap.children.length; c++) {
        if (wrap.children[c].tagName === 'LABEL') { label = wrap.children[c]; break; }
      }
    }
    var valueSpan = parts.btn.querySelector('.' + parts.prefix + '-cdd-trigger__label');
    if (label) {
      if (!label.id) label.id = cdd.id + '-label';
      if (valueSpan && !valueSpan.id) valueSpan.id = cdd.id + '-value';
      parts.btn.setAttribute('aria-labelledby', label.id + (valueSpan ? ' ' + valueSpan.id : ''));
      parts.btn.removeAttribute('aria-label');
      if (!label._ifungiFocusTrigger) {
        label._ifungiFocusTrigger = true;
        label.addEventListener('click', function (event) {
          event.preventDefault();
          parts.btn.focus();
        });
      }
    }

    /* Defensive only: the markup already sets both of these on the native
       select. Re-applying costs nothing and keeps this file correct if the
       markup is ever regenerated without them. */
    if (parts.sel.getAttribute('tabindex') !== '-1') parts.sel.setAttribute('tabindex', '-1');
    if (parts.sel.getAttribute('aria-hidden') !== 'true') parts.sel.setAttribute('aria-hidden', 'true');
    return true;
  }

  function init() {
    var list = document.querySelectorAll(CDD);
    for (var i = 0; i < list.length; i++) {
      try { wire(list[i]); } catch (e) {
        /* Leave this dropdown exactly as it was rather than half-converted. */
        if (window.console) console.warn('[ifungi-a11y] could not wire a dropdown:', e);
      }
    }
  }

  /* Bind at window capture phase so purchase-intent guards on document cannot stop
     Arrow/Home/End navigation before the custom listbox receives it. */
  window.addEventListener('keydown', onKeydown, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
